import { useReceiptStore } from "@elixir/stores";
import { RECEIPT_FLOW_STATES } from "@elixir/types";
import { InputFormField, FormField, Chip } from "ui";
import { useState, useEffect, useMemo } from "react";
import PhoneCountryCodeDropdown from "../phone-number-dropdown";
import {
  CountryCode,
  getCountryCodesData,
} from "../../../constants/country-code-data";

export default function ReceiptInputParams({
  disableName,
  disablePhone,
  hidePaymentStatus = false,
}: {
  disableName?: boolean;
  disablePhone?: boolean;
  hidePaymentStatus?: boolean;
}) {
  const patientName = useReceiptStore((state) => state.patientName);
  const setPatientName = useReceiptStore((state) => state.setPatientName);
  const uhid = useReceiptStore((state) => state.uhid);
  const phoneNumber = useReceiptStore((state) => state.phoneNumber);
  const fullPhoneNumber = useReceiptStore((state) => state.fullPhoneNumber);
  const mobile = fullPhoneNumber || phoneNumber;
  const setMobile = useReceiptStore((state) => state.setPhoneNumber);
  const setFullMobile = useReceiptStore((state) => state.setFullPhoneNumber);
  const receiptState = useReceiptStore((state) => state.receiptState);

  const countryCodesData = useMemo(() => getCountryCodesData(), []);

  const extractCountryCodeFromPhone = (
    phoneNumber: string | undefined
  ): { code: string; country: CountryCode } | null => {
    if (!phoneNumber || !phoneNumber.startsWith("+")) {
      return null;
    }

    const sortedCountryCodes = [...countryCodesData].sort(
      (a, b) => b.countryCallingCode.length - a.countryCallingCode.length
    );

    for (const country of sortedCountryCodes) {
      const codeWithPlus = `+${country.countryCallingCode}`;
      if (phoneNumber.startsWith(codeWithPlus)) {
        return { code: codeWithPlus, country: country.countryCode };
      }
    }

    return null;
  };

  const extractedCountryCode = useMemo(() => {
    return extractCountryCodeFromPhone(fullPhoneNumber);
  }, [fullPhoneNumber, countryCodesData]);

  const [countryCode, setCountryCode] = useState<string>(() => {
    return extractedCountryCode?.code || "+91";
  });

  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => {
    return extractedCountryCode?.country || "IN";
  });

  useEffect(() => {
    if (extractedCountryCode) {
      setCountryCode(extractedCountryCode.code);
      setSelectedCountry(extractedCountryCode.country);
    } else if (!mobile) {
      setCountryCode("+91");
      setSelectedCountry("IN");
    }
  }, [extractedCountryCode, fullPhoneNumber]);

  const getNumberWithoutCountryCode = (): string => {
    if (!mobile) return "";
    // Check if mobile starts with current countryCode
    if (mobile?.startsWith(countryCode)) {
      return mobile?.slice(countryCode.length);
    }
    // If not, try to extract any country code from mobile
    const extracted = extractCountryCodeFromPhone(mobile);
    if (extracted) {
      return mobile.slice(extracted.code.length);
    }
    // If no country code found, return mobile as-is
    return mobile;
  };

  const handleCountryCodeSelect = (code: string, country: CountryCode) => {
    const currentNumber = getNumberWithoutCountryCode();
    setCountryCode(code);
    setSelectedCountry(country);
    // mobile with new country code + existing number
    if (currentNumber) {
      const fullNumber = code + currentNumber;
      setMobile(fullNumber);
      setFullMobile?.(fullNumber);
    }
  };

  return (
    <div className="dr-flex dr-space-x-20 dr-items-center dr-px-16 dr-py-16">
      <InputFormField
        title="Name"
        isRequired
        width={170}
        value={patientName}
        onChange={(e) => setPatientName(e.target.value)}
        isDisabled={disableName}
      />
      <InputFormField title="UHID" width={150} isDisabled value={uhid} />
      <FormField title="Phone" isRequired>
        <div className="dr-flex dr-border dr-border-darwin-touch-neutral-400 dr-rounded-4 dr-overflow-hidden dr-h-36">
          <div className="dr-flex dr-items-center dr-border-r dr-border-darwin-touch-neutral-400">
            <PhoneCountryCodeDropdown
              value={countryCode}
              onSelect={handleCountryCodeSelect}
              disabled={disablePhone}
            />
          </div>
          <input
            type="text"
            className="dr-px-8 dr-outline-none dr-w-130 dr-text-darwin-touch-neutral-900"
            placeholder="Enter phone number"
            value={getNumberWithoutCountryCode()}
            disabled={disablePhone}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 10) {
                const fullNumber = value ? countryCode + value : "";
                setMobile(fullNumber);
                setFullMobile?.(fullNumber);
              }
            }}
          />
        </div>
      </FormField>
      {hidePaymentStatus ? null : (
        <FormField title="Payment Status">
          <div className="dr-max-w-min">
            {[
              RECEIPT_FLOW_STATES.CREATE_RECEIPT,
              RECEIPT_FLOW_STATES.SEND_PAYMENT_LINK,
              RECEIPT_FLOW_STATES.SETTLE_DUES,
            ].includes(receiptState) ? (
              <Chip
                title="Unbilled"
                borderRadius="dr-rounded-4"
                className="dr-text-darwin-neutral-600 dr-bg-darwin-neutral-50"
              />
            ) : (
              <Chip
                title="Paid"
                borderRadius="dr-rounded-4"
                className="dr-text-darwin-green-primary dr-bg-darwin-green-bg-light"
              />
            )}
          </div>
        </FormField>
      )}
    </div>
  );
}
